/**
 * KAN-834: лента переписки в карточке клиента приложения.
 * KAN-858: лента целиком (подгрузка ранних), ручная переписка в MobileClientChat.
 * KAN-929: звонки менеджера (feed.calls) идут в ленте между сообщениями по
 * времени блоком CallFeedItem (запись по "Слушать", расшифровка); отдельного
 * блока "Звонки" больше нет. Пока выше есть незагруженные сообщения, звонки
 * раньше загруженных скрыты (mergeFeedWithCalls).
 * KAN-912: с clientId на нашем удаляемом сообщении кнопка-меню "Удалить"
 * размером под палец (useMessageDeletion), подтверждение в DeleteMessageDialog.
 */
import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { CallFeedItem } from '@/components/messages/CallFeedItem';
import { DeleteMessageDialog } from '@/components/messages/DeleteMessageDialog';
import { MessageBubble } from '@/components/messages/MessageBubble';
import { formatAttentionDate } from '@/components/dashboard/attentionFormat';
import { useChatFeedScroll } from '@/hooks/useChatFeedScroll';
import { useMessageDeletion } from '@/hooks/useMessageDeletion';
import type { MobileClientFeed } from '@/hooks/useMobileClientFeed';
import { mergeFeedWithCalls } from '@/lib/conversationFeed';
import { LOAD_EARLIER_LABEL } from '@/lib/mobileChat';
import { channelShortLabel } from '@/lib/messageChannel';

/**
 * KAN-858: вся лента переписки в своей прокрутке: последние внизу, сверху
 * "Показать ранние сообщения" (и сама подгрузка при прокрутке к верху).
 */
export function MobileChatFeed({ feed, clientId }: { feed: MobileClientFeed; clientId?: string }): JSX.Element {
  const deletion = useMessageDeletion(clientId);
  const { messages, calls, isLoading, isError, hasMore, isLoadingMore, loadMore } = feed;
  const items = useMemo(() => mergeFeedWithCalls(messages, calls, { hasMore }), [messages, calls, hasMore]);
  const { ref, onScroll } = useChatFeedScroll({
    lastId: items[items.length - 1]?.id,
    firstId: items[0]?.id,
    onReachTop: loadMore,
  });
  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className="max-h-[60vh] space-y-2 overflow-y-auto overscroll-contain"
      data-testid="mobile-messages"
    >
      {isLoading && <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" aria-label="Загрузка" />}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить переписку</p>}
      {!isLoading && !isError && items.length === 0 && <p className="text-sm text-gray-500">Сообщений нет</p>}
      {hasMore && (
        <button
          type="button"
          onClick={loadMore}
          className="mx-auto flex min-h-[40px] items-center rounded-lg px-3 text-sm text-blue-700 active:bg-blue-50"
          data-testid="mobile-messages-more"
        >
          {LOAD_EARLIER_LABEL}
        </button>
      )}
      {isLoadingMore && (
        <Loader2 className="mx-auto h-4 w-4 animate-spin text-gray-400" aria-label="Загрузка ранних сообщений" />
      )}
      {items.map((item) =>
        item.kind === 'call' ? (
          <CallFeedItem key={`call-${item.id}`} call={item.call} time={formatAttentionDate(item.at)} />
        ) : (
          <MessageBubble
            key={item.id}
            message={item.message}
            channel={<span>{channelShortLabel(item.message.source)}</span>}
            time={formatAttentionDate(item.message.created_at)}
            onDelete={deletion.handlerFor(item.message)}
            touchActions
          />
        )
      )}
      {clientId && <DeleteMessageDialog clientId={clientId} message={deletion.target} onClose={deletion.close} />}
    </div>
  );
}
